import {
	getGraphqlErrorAttributes,
	getGraphqlReportedErrors,
	getLogSafeErrorAttributes,
} from '@confluence/monitoring/entry-points/error-attributes.ts';
import { isErrorIgnorableByOnCall } from '@confluence/monitoring/entry-points/isErrorIgnorableByOnCall.ts';
import { isGraphQLError } from '@confluence/network';

// Maps error-boundary attribution to bounded responder values for experience-alert routing.
// Add entries only when a distinct responder needs routing; every new value increases SignalFX tag cardinality.
const responderByErrorAttribution: Readonly<Record<string, string>> = {
	graphql: 'backend',
};

const mappedResponders = new Set(Object.values(responderByErrorAttribution));

// --- GraphQL edge case -------------------------------------------------------------------------
// GraphQL failures override the error-boundary attribution so they route like the FE error rate
// `attribution: 'graphql'` signal (see `GraphqlErrorProcessor`/`OnErrorLink`) does. To avoid drifting
// from it, this classifies the same per-error units `OnErrorLink` reports (`getGraphqlReportedErrors`)
// and applies the same on-call filter (`isErrorIgnorableByOnCall`).
function isActionableGraphQLError(error: Error | undefined): boolean {
	if (!error) return false;

	const reportedErrors = getGraphqlReportedErrors(error);
	// Anything unwrapped from an Apollo error came from a GraphQL request. Otherwise the error has to
	// be a bare (Relay) `GraphQLError`, or carry the attributes `OnErrorLink` sets on GraphQL errors.
	const isUnwrappedFromApollo = reportedErrors[0] !== error;

	return reportedErrors.some(
		(reportedError) =>
			(isUnwrappedFromApollo ||
				isGraphQLError(reportedError) ||
				getGraphqlErrorAttributes(reportedError) !== undefined) &&
			!isErrorIgnorableByOnCall(reportedError, 'graphql', getLogSafeErrorAttributes(reportedError)),
	);
}
// ------------------------------------------------------------------------------------------------

export function getResponderForErrorAttribution(
	errorAttribution: unknown,
	error?: Error,
): string | undefined {
	const attribution = isActionableGraphQLError(error) ? 'graphql' : errorAttribution;
	return typeof attribution === 'string' ? responderByErrorAttribution[attribution] : undefined;
}

export function isMappedResponder(value: unknown): value is string {
	return typeof value === 'string' && mappedResponders.has(value);
}
